<template>
	<article
		:id="`card-${psychologist.idByAddress}`"
		:class="{ card: true, 'card--selected': isSelected() }"
		@click="selectPsychologist"
	>
		<!-- Prénom NOM -->
		<p
			class="text-h4 font-weight-bold"
			style="margin: 0"
		>
			{{ psychologist.firstname }} {{ psychologist.lastname }}
		</p>

		<v-divider style="margin: 15px 0" />

		<!-- Adresse -->
		<Address
			v-if="psychologist.address"
			:address="psychologist.address"
			:address-additional="psychologist.address_additional"
			:style="getAddressStyle('from_first_address')"
		/>

		<!-- Adresse secondaire -->
		<Address
			v-if="psychologist.secondAddress"
			:address="psychologist.secondAddress"
			:address-additional="psychologist.second_address_additional"
			:style="getAddressStyle('from_second_address')"
		/>

		<!-- Bouton de consultation -->
		<ConsultButton
			:psychologist="psychologist"
		/>

		<!-- Public accepté -->
		<IconText
			v-if="psychologist.public"
			:icon="publicIcon"
			:text="`Accompagne des ${psychologist.public.toLowerCase()}`"
			icon-color="yellow"
		/>

		<!-- Téléconsultation -->
		<IconText
			v-if="psychologist.teleconsultation"
			:icon="teleconsultationIcon"
			icon-color="yellow"
			text="Possibilité de séances à distance"
		/>

		<!-- Langues parlées -->
		<IconText
			v-if="psychologist.languages"
			:icon="languagesIcon"
			:text="psychologist.languages"
			icon-color="yellow"
		/>
	</article>
</template>

<script lang="ts">
	import { Component, Prop, Vue } from 'vue-property-decorator';
	import { VDivider } from 'vuetify/lib';
	import { mdiAccountGroup, mdiChat, mdiVideo } from '@mdi/js';
	import { SearchPsychologist } from '@/types/models/SearchPsychologist';
	import Address from '@/components/search-psychologist/result-section/card-content/Address.vue';
	import ConsultButton from '@/components/search-psychologist/result-section/card-content/ConsultButton.vue';
	import IconText from '@/components/search-psychologist/result-section/card-content/IconText.vue';
	import { AddressChoice } from '@/types';
	import { PsychologistService } from '@/services/psychologist-service';

	/**
	 * Carte d'un psychologue
	 */
	@Component({
		components: { IconText, ConsultButton, Address, VDivider }
	})
	export default class PsychologistCard extends Vue {
		// Psychologue
		@Prop({ default: {} })
		readonly psychologist!: SearchPsychologist;

		// Id du psychologue sélectionné
		@Prop()
		readonly selectedPsychologistIdByAddress!: string;

		// Icônes
		readonly publicIcon = mdiAccountGroup;
		readonly teleconsultationIcon = mdiVideo;
		readonly languagesIcon = mdiChat;

		/**
		 * Envoie l'id du psychologue au parent
		 */
		selectPsychologist(): void {
			this.$emit('selected-psychologist-id', this.psychologist.idByAddress);
		}

		/**
		 * Permet de savoir si le psychologue est sélectionné
		 *
		 * @returns Vrai si le psychologue est sélectionné, faux sinon
		 */
		isSelected(): boolean {
			const EXPECTED_PARTS_COUNT = 2;

			return this.selectedPsychologistIdByAddress?.split(';').length === EXPECTED_PARTS_COUNT
				? PsychologistService.getCoordinatesFromPsychologistIdByAddress(this.psychologist.idByAddress) === this.selectedPsychologistIdByAddress
				: this.psychologist.idByAddress === this.selectedPsychologistIdByAddress;
		}

		/**
		 * Retourne le style à appliquer à l'adresse
		 *
		 * @param addressChoice Choix de l'adresse
		 * @returns Style à appliquer
		 */
		private getAddressStyle(addressChoice: AddressChoice): object {
			const isBold = (
				(addressChoice === 'from_first_address' && this.psychologist.origin === 'from_first_address') ||
				(addressChoice === 'from_second_address' && this.psychologist.origin === 'from_second_address')
			);

			if (this.psychologist.address && this.psychologist.secondAddress && isBold) {
				return { 'font-weight': 'bold' };
			}

			return {};
		}
	}
</script>

<style lang="css" scoped>
.card {
	cursor: pointer;
	border: 1px solid #eeeeee;
	display: flex;
	gap: 0.2rem;
	flex-direction: column;
	padding: 1rem;
	width: 100%;
	border-bottom: 4px solid #0c419a;
	scroll-snap-align: start;
}

.card:hover {
	background-color: #e8f1ff;
}

.card--selected {
	background-color: #e8f1ff;
	border: 2px solid #0c419a;
}

@media screen and (max-width: 768px) {
	.card {
		scroll-snap-align: center;
		flex: 0 0 60%;
		height: auto;
		max-height: 400px;
		overflow: auto;
	}
}
</style>
